"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Overlay = exports.ScrollingRootContext = exports.HorizontalOriginOption = exports.VerticalOriginOption = void 0;
var tslib_1 = require("tslib");
// tslint:disable:deprecation
var throttle = require("lodash/throttle");
var React = require("react");
var ReactDOM = require("react-dom");
var tabbable = require("tabbable");
var StackingComponent_1 = require("./StackingComponent");
var ViewportHelpers = require("./ViewportHelpers");
var ResizeThrottleTime = 100;
var ScrollThrottleTime = 15;
var VerticalOriginOption;
(function (VerticalOriginOption) {
    VerticalOriginOption["top"] = "top";
    VerticalOriginOption["centerY"] = "centerY";
    VerticalOriginOption["bottom"] = "bottom";
})(VerticalOriginOption = exports.VerticalOriginOption || (exports.VerticalOriginOption = {}));
var HorizontalOriginOption;
(function (HorizontalOriginOption) {
    HorizontalOriginOption["left"] = "left";
    HorizontalOriginOption["centerX"] = "centerX";
    HorizontalOriginOption["right"] = "right";
})(HorizontalOriginOption = exports.HorizontalOriginOption || (exports.HorizontalOriginOption = {}));
exports.ScrollingRootContext = React.createContext(document.body);
exports.ScrollingRootContext.displayName = 'ScrollingRootContext';
function getScrollingRootElement(scrollingRootId, context) {
    var scrollingRoot = document.getElementById(scrollingRootId) || context || document.body;
    if (!scrollingRoot) {
        throw new Error('Could not find scrolling root.');
    }
    return scrollingRoot;
}
var Overlay = /** @class */ (function (_super) {
    tslib_1.__extends(Overlay, _super);
    function Overlay() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.scrollingRootElement = null;
        _this.onResize = throttle(function () { return _this.setPlacement(); }, ResizeThrottleTime);
        /**
         * Once the stacking wrapper mounts it returns itself and we set the placement in case the stacked element is already open
         */
        _this.setStackingWrapper = function (stackingWrapper) {
            _this.stackingWrapper = stackingWrapper;
            _this.setPlacement();
        };
        return _this;
    }
    Overlay.prototype.componentDidMount = function () {
        this.addScrollingRootBehavior();
        window.addEventListener('resize', this.onResize);
        if (this.props.autoFocus && this.stackingWrapper) {
            var wrapperNode = ReactDOM.findDOMNode(this.stackingWrapper);
            var tabbables = tabbable(wrapperNode);
            var node = tabbables[0] || wrapperNode.querySelector('[tabindex]');
            if (node) {
                node.focus();
            }
        }
    };
    Overlay.prototype.componentWillUnmount = function () {
        this.removeScrollingRootBehavior();
        window.removeEventListener('resize', this.onResize);
    };
    Overlay.prototype.componentDidUpdate = function (prevProps) {
        this.resetScrollingRootBehaviorIfNeeded(prevProps);
        this.setPlacement();
    };
    /**
     * This adds the scrolling event listeners to the scrolling root if onScroll is not null.
     */
    Overlay.prototype.addScrollingRootBehavior = function () {
        this.setOnScroll();
        if (!!this.onScroll) {
            this.scrollingRootElement = getScrollingRootElement(this.props.scrollingRootId, this.context);
            this.scrollingRootElement.addEventListener('scroll', this.onScroll);
            this.scrollingRootElement.addEventListener('wheel', this.onScroll);
        }
    };
    /**
     * This removes the scrolling event listeners to the scrolling root if onScroll is not null.
     */
    Overlay.prototype.removeScrollingRootBehavior = function () {
        var _a, _b;
        if (!!this.onScroll) {
            (_a = this.scrollingRootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', this.onScroll);
            (_b = this.scrollingRootElement) === null || _b === void 0 ? void 0 : _b.removeEventListener('wheel', this.onScroll);
            this.scrollingRootElement = null;
        }
    };
    /**
     * This resets the scrolling root behavior if handleScrollOnScrollingRoot or shouldAutoPositionOnScrollingRootScroll has changed.
     */
    Overlay.prototype.resetScrollingRootBehaviorIfNeeded = function (prevProps) {
        if (prevProps.handleScrollOnScrollingRoot !== this.props.handleScrollOnScrollingRoot ||
            prevProps.shouldAutoPositionOnScrollingRootScroll !== this.props.shouldAutoPositionOnScrollingRootScroll) {
            this.removeScrollingRootBehavior();
            this.addScrollingRootBehavior();
        }
    };
    /**
     * Assigns onScroll to a method if shouldAutoPositionOnScrollingRootScroll is true or handleScrollOnScrollingRoot is not null.
     * Otherwise assign it to null.
     */
    Overlay.prototype.setOnScroll = function () {
        var _this = this;
        if (this.props.shouldAutoPositionOnScrollingRootScroll || !!this.props.handleScrollOnScrollingRoot) {
            var onScroll = function () {
                if (_this.props.shouldAutoPositionOnScrollingRootScroll) {
                    _this.setPlacement();
                }
                if (!!_this.props.handleScrollOnScrollingRoot) {
                    _this.props.handleScrollOnScrollingRoot();
                }
            };
            this.onScroll = throttle(onScroll, ScrollThrottleTime);
        }
        else {
            this.onScroll = null;
        }
    };
    /**
     * Returns the position of the anchor element
     */
    Overlay.prototype.getAnchorPosition = function () {
        var anchorClientRect = undefined;
        if (this.props.getAnchorClientRect) {
            anchorClientRect = this.props.getAnchorClientRect();
        }
        if (anchorClientRect === undefined) {
            // This will allow stacked elements to be placed locally if no anchor element is provided
            var anchorEl = this.props.anchorEl || ReactDOM.findDOMNode(this);
            anchorClientRect = anchorEl.getBoundingClientRect();
        }
        var left = anchorClientRect.left, top = anchorClientRect.top, right = anchorClientRect.right, bottom = anchorClientRect.bottom;
        return {
            top: top,
            centerY: top + (bottom - top) / 2,
            bottom: bottom,
            left: left,
            centerX: left + (right - left) / 2,
            right: right,
        };
    };
    /**
     * Returns the shape of the element that has been stacked in position coordinates. So the top left of the shape
     * is top: 0, left: 0, the right corner is top: 0, right: width, and so on.
     */
    Overlay.prototype.getStackedElCurrentShapeInPositionCoords = function () {
        var stackedEl = this.getStackedEl();
        return {
            top: 0,
            centerY: stackedEl.offsetHeight / 2,
            bottom: stackedEl.offsetHeight,
            left: 0,
            centerX: stackedEl.offsetWidth / 2,
            right: stackedEl.offsetWidth,
        };
    };
    /**
     * Returns the rectangle of the container
     */
    Overlay.prototype.getContainerRect = function () {
        return this.stackingWrapper.getStackingRoot().getBoundingClientRect();
    };
    /**
     * Sets the stacked element position
     */
    Overlay.prototype.setPlacement = function () {
        if (!this.stackingWrapper || !this.stackingWrapper.getMountPoint()) {
            return;
        }
        // TFSID 1083485: Some elements change their size when positioned absolutely.
        // In order to accurately set their position, we need to position them absolutely first.
        var stackedEl = this.getStackedEl();
        stackedEl.style.position = 'absolute';
        var stackedElAdjustedPosition = this.getStackedElAdjustedPosition();
        stackedEl.style.top = stackedElAdjustedPosition.top + "px";
        stackedEl.style.left = stackedElAdjustedPosition.left + "px";
        if (this.props.disablePointerEvents) {
            stackedEl.style.pointerEvents = 'none';
        }
    };
    /**
     * Gets the stacked element from stacking wrapper
     */
    Overlay.prototype.getStackedEl = function () {
        return this.stackingWrapper.getMountPoint().firstChild;
    };
    /**
     * Returns the Stacked Element position that has been autopositioned if necessary and adjusted to position
     * relative to the container
     */
    Overlay.prototype.getStackedElAdjustedPosition = function () {
        var viewportPosition = this.getDefaultStackedElViewportPosition();
        if (this.props.allowAutoPosition) {
            viewportPosition = this.getAutoPositionIfNeeded(viewportPosition);
        }
        return this.getAdjustStackedElPositionForContainer(viewportPosition);
    };
    /**
     * Returns the stacked element viewport position from the given and default origin option props
     */
    Overlay.prototype.getDefaultStackedElViewportPosition = function () {
        var stackedElPosition = this.getStackedElViewportPosition(this.props.anchorOrigin, this.props.stackedOrigin);
        if (!this.props.getAnchorClientRect) {
            stackedElPosition.top += (this.props.topFromOverlaidPosition || 0);
            stackedElPosition.left += (this.props.leftFromOverlaidPosition || 0);
        }
        return stackedElPosition;
    };
    /**
     * Adjusts the viewport position so that it can be absolutely positioned relative to the container
     */
    Overlay.prototype.getAdjustStackedElPositionForContainer = function (viewportPosition) {
        var containerRect = this.getContainerRect();
        var newPosition = {
            top: viewportPosition.top - containerRect.top,
            left: viewportPosition.left - containerRect.left
        };
        return newPosition;
    };
    /**
     * Using the given origins return the adjusted stacked element viewport position
     */
    Overlay.prototype.getStackedElViewportPosition = function (anchorOriginOptions, stackedElOriginOptions) {
        var anchorPosition = this.getAnchorPosition();
        var stackedElShape = this.getStackedElCurrentShapeInPositionCoords();
        // This gets the origin strings from the enum (eg 'top', 'left', etc)
        var anchorVerticalOriginOption = VerticalOriginOption[anchorOriginOptions.vertical];
        var anchorHorizontalOriginOption = HorizontalOriginOption[anchorOriginOptions.horizontal];
        var stackedElVerticalOriginOption = VerticalOriginOption[stackedElOriginOptions.vertical];
        var stackedElHorizontalOriginOption = HorizontalOriginOption[stackedElOriginOptions.horizontal];
        // This gets the top/left for the anchor/stackedEl origins,
        // similar to OriginPositionCoordinates.left, OriginPositionCoordinates.top, etc
        var anchorOriginTop = anchorPosition[anchorVerticalOriginOption];
        var anchorOriginLeft = anchorPosition[anchorHorizontalOriginOption];
        var stackedElOriginTop = stackedElShape[stackedElVerticalOriginOption];
        var stackedElOriginLeft = stackedElShape[stackedElHorizontalOriginOption];
        var adjustedStackedPosition = {
            top: anchorOriginTop - stackedElOriginTop,
            left: anchorOriginLeft - stackedElOriginLeft,
        };
        return adjustedStackedPosition;
    };
    /**
     * This will try to autoposition the stacked element if the stacked element will appear off screen.
     * It autopositions by using the opposite origin options and getting that viewport position
     *
     * Example:
     * Anchor is at the bottom right of the screen with the anchor origin as {top, right} and the stacked el origin as {top, left}
     * Detects that it's vertically offscreen and changes the anchor origin to {bottom, right} and stacked el origin as {bottom, left}
     * Method sees that it is now not below the screen and sets the adjusted top position
     * Detects that it's horizontally offscreen and changes the anchor origin to {top, left} and stacked el origin as {top, right}
     * Method sees that it is now not to the right of the screen and sets the adjusted left position
     */
    Overlay.prototype.getAutoPositionIfNeeded = function (adjustedStackedElPosition) {
        var stackedElShape = this.getStackedElCurrentShapeInPositionCoords();
        var oppositeOriginPosition = this.getOppositeOriginPosition();
        var layoutViewportSize = ViewportHelpers.getLayoutViewportSize();
        var isVerticallyOffscreen = adjustedStackedElPosition.top < 0 ||
            adjustedStackedElPosition.top + stackedElShape.bottom > layoutViewportSize.height;
        var isHorizontallyOffscreen = adjustedStackedElPosition.left < 0 ||
            adjustedStackedElPosition.left + stackedElShape.right > layoutViewportSize.width;
        if (isVerticallyOffscreen) {
            if (oppositeOriginPosition.top + stackedElShape.bottom <= layoutViewportSize.height) {
                adjustedStackedElPosition.top = Math.max(0, oppositeOriginPosition.top);
            }
            else if (stackedElShape.bottom <= layoutViewportSize.height) {
                adjustedStackedElPosition.top = Math.max(0, layoutViewportSize.height - stackedElShape.bottom);
            }
        }
        if (isHorizontallyOffscreen) {
            if (oppositeOriginPosition.left + stackedElShape.right <= layoutViewportSize.width) {
                adjustedStackedElPosition.left = Math.max(0, oppositeOriginPosition.left);
            }
            else if (stackedElShape.right <= layoutViewportSize.width) {
                adjustedStackedElPosition.left = Math.max(0, layoutViewportSize.width - stackedElShape.right);
            }
        }
        return adjustedStackedElPosition;
    };
    /**
     * Uses the opposite origins for the anchor and stacked element to return the viewport position
     */
    Overlay.prototype.getOppositeOriginPosition = function () {
        var oppositeAnchorOriginOptions = this.getOppositeOriginsOptions(this.props.anchorOrigin);
        var oppositeStackedElOriginOptions = this.getOppositeOriginsOptions(this.props.stackedOrigin);
        var stackedElPosition = this.getStackedElViewportPosition(oppositeAnchorOriginOptions, oppositeStackedElOriginOptions);
        if (!this.props.getAnchorClientRect) {
            stackedElPosition.top -= (this.props.topFromOverlaidPosition || 0);
            stackedElPosition.left -= (this.props.leftFromOverlaidPosition || 0);
        }
        return stackedElPosition;
    };
    /**
     * Returns the opposite origin options
     */
    Overlay.prototype.getOppositeOriginsOptions = function (originOptions) {
        return {
            horizontal: this.getOppositeHorizontalOption(originOptions.horizontal),
            vertical: this.getOppositeVerticalOption(originOptions.vertical)
        };
    };
    /**
     * Returns the opposite VerticalOriginOption, unless it is center.
     */
    Overlay.prototype.getOppositeVerticalOption = function (verticalOriginOption) {
        if (verticalOriginOption === VerticalOriginOption.centerY) {
            return VerticalOriginOption.centerY;
        }
        return verticalOriginOption === VerticalOriginOption.top ? VerticalOriginOption.bottom : VerticalOriginOption.top;
    };
    /**
     * Returns the opposite HorizontalOriginOption, unless it is center.
     */
    Overlay.prototype.getOppositeHorizontalOption = function (horizontalOriginOption) {
        if (horizontalOriginOption === HorizontalOriginOption.centerX) {
            return HorizontalOriginOption.centerX;
        }
        return horizontalOriginOption === HorizontalOriginOption.left ? HorizontalOriginOption.right : HorizontalOriginOption.left;
    };
    Overlay.prototype.render = function () {
        var wrapperId = this.props.stackingRootId ? this.props.stackingRootId + "-" : '';
        wrapperId = this.props.scrollingRootId ? "" + wrapperId + this.props.scrollingRootId + "-" : wrapperId;
        return React.createElement(StackingComponent_1.StackingComponentWrapper, { children: this.props.children, portalEventTrapOverrides: this.props.portalEventTrapOverrides, ref: this.setStackingWrapper, stackingRootId: this.props.stackingRootId, wrapperTestId: wrapperId + "Overlay", zIndex: this.props.zIndex });
    };
    Overlay.defaultProps = {
        anchorOrigin: {
            vertical: VerticalOriginOption.top,
            horizontal: HorizontalOriginOption.left
        },
        stackedOrigin: {
            vertical: VerticalOriginOption.top,
            horizontal: HorizontalOriginOption.left
        }
    };
    return Overlay;
}(React.Component));
exports.Overlay = Overlay;
Overlay.contextType = exports.ScrollingRootContext;
//# sourceMappingURL=Overlay.js.map